"use client"

import * as React from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { DataTable } from '@open-mercato/ui/backend/DataTable'
import type { ColumnDef } from '@tanstack/react-table'
import { Button } from '@open-mercato/ui/primitives/button'
import { RowActions } from '@open-mercato/ui/backend/RowActions'
import { apiCall, apiCallOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useOrganizationScopeVersion } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useConfirmDialog } from '@open-mercato/ui/backend/confirm-dialog'
import { useGuardedMutation } from '@open-mercato/ui/backend/injection/useGuardedMutation'
import type { FilterDef, FilterValues } from '@open-mercato/ui/backend/FilterBar'

type SlotRow = {
  id: string
  stationId: string
  stationName: string | null
  slotType: string
  status: string
  trackNumber: number
  platform: string | null
  startTime: string
  endTime: string
  trainPlanId: string | null
  cargoDescription: string | null
}

type SlotsResponse = {
  items: Array<Record<string, unknown>>
  total: number
  page: number
  totalPages: number
}

function mapSlotRow(item: Record<string, unknown>): SlotRow | null {
  const id = typeof item.id === 'string' ? item.id : null
  if (!id) return null
  return {
    id,
    stationId: typeof item.station_id === 'string' ? item.station_id : '',
    stationName: typeof item.station_name === 'string' ? item.station_name : null,
    slotType: typeof item.slot_type === 'string' ? item.slot_type : '',
    status: typeof item.status === 'string' ? item.status : 'planned',
    trackNumber: typeof item.track_number === 'number' ? item.track_number : 0,
    platform: typeof item.platform === 'string' ? item.platform : null,
    startTime: typeof item.start_time === 'string' ? item.start_time : '',
    endTime: typeof item.end_time === 'string' ? item.end_time : '',
    trainPlanId: typeof item.train_plan_id === 'string' ? item.train_plan_id : null,
    cargoDescription: typeof item.cargo_description === 'string' ? item.cargo_description : null,
  }
}

const STATUS_CLASS: Record<string, string> = {
  planned: 'text-muted-foreground',
  booked: 'text-blue-600',
  in_progress: 'text-orange-600',
  completed: 'text-green-600',
  cancelled: 'text-red-600',
}

export default function TerminalSlotsPage() {
  const { confirm, ConfirmDialogElement } = useConfirmDialog()
  const { runMutation } = useGuardedMutation<Record<string, unknown>>({
    contextId: 'terminal-scheduling-list',
  })
  const [rows, setRows] = React.useState<SlotRow[]>([])
  const [page, setPage] = React.useState(1)
  const pageSize = 50
  const [total, setTotal] = React.useState(0)
  const [totalPages, setTotalPages] = React.useState(1)
  const [search, setSearch] = React.useState('')
  const [filterValues, setFilterValues] = React.useState<FilterValues>({})
  const [isLoading, setIsLoading] = React.useState(true)
  const [reloadToken, setReloadToken] = React.useState(0)
  const scopeVersion = useOrganizationScopeVersion()
  const t = useT()
  const router = useRouter()

  const getStatusLabel = React.useCallback((status: string) => {
    const fallbackMap: Record<string, string> = {
      planned: 'Planned',
      booked: 'Booked',
      in_progress: 'In Progress',
      completed: 'Completed',
      cancelled: 'Cancelled',
    }
    return t(`terminal_scheduling.slots.status.${status}`, fallbackMap[status] ?? status)
  }, [t])

  const getSlotTypeLabel = React.useCallback((slotType: string) => {
    const fallbackMap: Record<string, string> = {
      arrival: 'Arrival',
      departure: 'Departure',
      shunting: 'Shunting',
    }
    return t(`terminal_scheduling.slots.type.${slotType}`, fallbackMap[slotType] ?? slotType)
  }, [t])

  const filters = React.useMemo<FilterDef[]>(() => [
    {
      id: 'slotType',
      label: t('terminal_scheduling.slots.filters.slotType', 'Slot Type'),
      type: 'select' as const,
      options: [
        { value: 'arrival', label: getSlotTypeLabel('arrival') },
        { value: 'departure', label: getSlotTypeLabel('departure') },
        { value: 'shunting', label: getSlotTypeLabel('shunting') },
      ],
    },
    {
      id: 'status',
      label: t('terminal_scheduling.slots.filters.status', 'Status'),
      type: 'select' as const,
      options: [
        { value: 'planned', label: getStatusLabel('planned') },
        { value: 'booked', label: getStatusLabel('booked') },
        { value: 'in_progress', label: getStatusLabel('in_progress') },
        { value: 'completed', label: getStatusLabel('completed') },
        { value: 'cancelled', label: getStatusLabel('cancelled') },
      ],
    },
  ], [getSlotTypeLabel, getStatusLabel, t])

  const queryParams = React.useMemo(() => {
    const params = new URLSearchParams()
    params.set('page', String(page))
    params.set('pageSize', String(pageSize))
    if (search.trim()) params.set('search', search.trim())
    const slotType = filterValues.slotType
    if (typeof slotType === 'string' && slotType.trim()) params.set('slotType', slotType)
    const status = filterValues.status
    if (typeof status === 'string' && status.trim()) params.set('status', status)
    return params.toString()
  }, [filterValues, page, search])

  React.useEffect(() => {
    let cancelled = false
    async function load() {
      setIsLoading(true)
      try {
        const fallback: SlotsResponse = { items: [], total: 0, page: 1, totalPages: 1 }
        const call = await apiCall<SlotsResponse>(
          `/api/terminal_scheduling/slots?${queryParams}`,
          undefined,
          { fallback },
        )
        if (!call.ok) {
          const errorPayload = call.result as { error?: string } | undefined
          const message = typeof errorPayload?.error === 'string'
            ? errorPayload.error
            : t('terminal_scheduling.slots.error.load', 'Failed to load terminal slots')
          flash(message, 'error')
          return
        }
        const payload = call.result ?? fallback
        if (!cancelled) {
          const items = Array.isArray(payload.items) ? payload.items : []
          setRows(items.map(mapSlotRow).filter((row): row is SlotRow => row !== null))
          setTotal(typeof payload.total === 'number' ? payload.total : items.length)
          setTotalPages(typeof payload.totalPages === 'number' ? payload.totalPages : 1)
        }
      } catch (error) {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : t('terminal_scheduling.slots.error.load', 'Failed to load terminal slots')
          flash(message, 'error')
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [queryParams, reloadToken, scopeVersion, t])

  const handleRefresh = React.useCallback(() => {
    setReloadToken((token) => token + 1)
  }, [])

  const handleDelete = React.useCallback(async (slot: SlotRow) => {
    if (!slot.id) return
    const confirmed = await confirm({
      title: t('terminal_scheduling.slots.deleteConfirm', 'Delete slot on track {{trackNumber}}?', { trackNumber: String(slot.trackNumber) }),
      variant: 'destructive',
    })
    if (!confirmed) return
    try {
      await runMutation({
        operation: () => apiCallOrThrow(
          `/api/terminal_scheduling/slots?id=${encodeURIComponent(slot.id)}`,
          { method: 'DELETE', headers: { 'content-type': 'application/json' } },
          { errorMessage: t('terminal_scheduling.slots.error.delete', 'Failed to delete slot') },
        ),
        context: {},
      })
      setRows((prev) => prev.filter((row) => row.id !== slot.id))
      setTotal((prev) => Math.max(prev - 1, 0))
      flash(t('terminal_scheduling.slots.flash.deleted', 'Terminal slot deleted'), 'success')
    } catch (error) {
      const message = error instanceof Error ? error.message : t('terminal_scheduling.slots.error.delete', 'Failed to delete slot')
      flash(message, 'error')
    }
  }, [confirm, runMutation, t])

  const handleFiltersApply = React.useCallback((values: FilterValues) => {
    setFilterValues(values)
    setPage(1)
  }, [])

  const handleFiltersClear = React.useCallback(() => {
    setFilterValues({})
    setPage(1)
  }, [])

  const formatDateTime = (iso: string) => {
    if (!iso) return '-'
    try {
      return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    } catch {
      return iso
    }
  }

  const columns = React.useMemo<ColumnDef<SlotRow>[]>(() => {
    const noValue = <span className="text-muted-foreground text-sm">-</span>
    return [
      {
        accessorKey: 'stationName',
        header: t('terminal_scheduling.slots.columns.station', 'Station'),
        cell: ({ row }) => row.original.stationName ?? row.original.stationId.slice(0, 8),
      },
      {
        accessorKey: 'slotType',
        header: t('terminal_scheduling.slots.columns.slotType', 'Type'),
        cell: ({ row }) => getSlotTypeLabel(row.original.slotType),
      },
      {
        accessorKey: 'status',
        header: t('terminal_scheduling.slots.columns.status', 'Status'),
        cell: ({ row }) => {
          const statusClass = STATUS_CLASS[row.original.status] ?? ''
          return <span className={statusClass}>{getStatusLabel(row.original.status)}</span>
        },
      },
      {
        accessorKey: 'trackNumber',
        header: t('terminal_scheduling.slots.columns.trackNumber', 'Track'),
        cell: ({ row }) => <span className="font-mono font-medium">{row.original.trackNumber}</span>,
      },
      {
        accessorKey: 'platform',
        header: t('terminal_scheduling.slots.columns.platform', 'Platform'),
        cell: ({ row }) => row.original.platform || noValue,
      },
      {
        accessorKey: 'startTime',
        header: t('terminal_scheduling.slots.columns.startTime', 'Start'),
        cell: ({ row }) => formatDateTime(row.original.startTime),
      },
      {
        accessorKey: 'endTime',
        header: t('terminal_scheduling.slots.columns.endTime', 'End'),
        cell: ({ row }) => formatDateTime(row.original.endTime),
      },
      {
        accessorKey: 'cargoDescription',
        header: t('terminal_scheduling.slots.columns.cargoDescription', 'Cargo'),
        cell: ({ row }) => row.original.cargoDescription || noValue,
      },
    ]
  }, [getSlotTypeLabel, getStatusLabel, t])

  return (
    <Page>
      <PageBody>
        <DataTable<SlotRow>
          title={t('terminal_scheduling.slots.title', 'Terminal Slots')}
          refreshButton={{
            label: t('terminal_scheduling.slots.actions.refresh', 'Refresh'),
            onRefresh: () => { setSearch(''); setPage(1); handleRefresh() },
          }}
          actions={(
            <Button asChild>
              <Link href="/backend/terminal-scheduling/create">
                {t('terminal_scheduling.slots.actions.create', 'New Slot')}
              </Link>
            </Button>
          )}
          columns={columns}
          data={rows}
          searchValue={search}
          onSearchChange={(value) => { setSearch(value); setPage(1) }}
          searchPlaceholder={t('terminal_scheduling.slots.searchPlaceholder', 'Search by cargo description or notes...')}
          filters={filters}
          filterValues={filterValues}
          onFiltersApply={handleFiltersApply}
          onFiltersClear={handleFiltersClear}
          perspective={{ tableId: 'terminal_scheduling.slots.list' }}
          onRowClick={(row) => router.push(`/backend/terminal-scheduling/${row.id}`)}
          rowActions={(row) => (
            <RowActions
              items={[
                {
                  id: 'delete',
                  label: t('terminal_scheduling.slots.actions.delete', 'Delete'),
                  destructive: true,
                  onSelect: () => { void handleDelete(row) },
                },
              ]}
            />
          )}
          pagination={{ page, pageSize, total, totalPages, onPageChange: setPage }}
          isLoading={isLoading}
        />
      </PageBody>
      {ConfirmDialogElement}
    </Page>
  )
}
